/**
 * WatchpointAlarmStep.tsx — the optional "Alarm" step of the create wizard.
 *
 * Lets the user create a starter alarm together with the monitor (pre-seeded when a
 * template is used). Reuses the same catalog-driven ConditionBuilder + channel
 * multiselect as the standalone alarm form, so conditions serialise identically.
 * The parent (WatchpointCreate) owns the state + performs the sequential create
 * (monitor → alarm) on submit.
 */
import { useEffect } from 'react';
import {
  Card, CardBody, CardHeader, CardTitle, CardDescription, Input, Switch, Skeleton,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { WatchpointFieldCatalog, AlarmSeverity } from '@/api/healthcheck';
import { ConditionBuilder } from '../alarms/ConditionBuilder';
import { ChannelMultiselect } from '../alarms/ChannelMultiselect';
import type { ConditionRow } from '../alarms/condition-serializer';

const SEVERITIES: AlarmSeverity[] = ['info', 'warning', 'critical'];

interface Props {
  catalog?: WatchpointFieldCatalog;
  catalogLoading: boolean;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  name: string;
  onNameChange: (v: string) => void;
  severity: AlarmSeverity;
  onSeverityChange: (v: AlarmSeverity) => void;
  rows: ConditionRow[];
  onRowsChange: (r: ConditionRow[]) => void;
  mode: 'and' | 'or';
  onModeChange: (m: 'and' | 'or') => void;
  channelIds: string[];
  onChannelIdsChange: (ids: string[]) => void;
  showErrors?: boolean;
}

export function WatchpointAlarmStep(p: Props) {
  // Seed one empty condition row once the catalog loads and the alarm is enabled
  // with no rows yet (mirrors the standalone alarm form).
  useEffect(() => {
    if (!p.enabled || !p.catalog || p.rows.length > 0) return;
    const first = p.catalog.condition_fields[0];
    if (first) p.onRowsChange([{ field: first.name, operator: first.operators[0] ?? '', value: '' }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.enabled, p.catalog]);

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Alarm</CardTitle>
        <CardDescription>Optionally create an alert rule for this monitor now.</CardDescription>
      </CardHeader>
      <CardBody>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', marginBottom: p.enabled ? 'var(--vn-space-4)' : 0 }}>
          <Switch checked={p.enabled} onCheckedChange={p.onEnabledChange} />
          <span style={{ fontWeight: 500 }}>Also create an alarm now</span>
        </label>

        {p.enabled && (
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Alarm name" required error={p.showErrors && !p.name.trim() ? 'Name is required.' : undefined}>
              {(f) => <Input {...f} value={p.name} onChange={(e) => p.onNameChange(e.target.value)} placeholder="5xx surge" style={{ maxWidth: 420 }} invalid={!!(p.showErrors && !p.name.trim())} />}
            </Field>

            <Field label="Severity">
              {() => (
                <Select value={p.severity} onValueChange={(v) => p.onSeverityChange(v as AlarmSeverity)}>
                  <SelectTrigger style={{ maxWidth: 220 }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <div>
              <div style={{ fontWeight: 500, marginBottom: 'var(--vn-space-2)' }}>Conditions</div>
              {p.catalogLoading || !p.catalog ? (
                <Skeleton style={{ height: 80 }} />
              ) : (
                <ConditionBuilder
                  catalog={p.catalog}
                  rows={p.rows}
                  mode={p.mode}
                  onRowsChange={p.onRowsChange}
                  onModeChange={p.onModeChange}
                  showErrors={p.showErrors}
                />
              )}
            </div>

            <div>
              <div style={{ fontWeight: 500, marginBottom: 'var(--vn-space-2)' }}>Notification channels</div>
              <ChannelMultiselect value={p.channelIds} onChange={p.onChannelIdsChange} />
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
